/**
 * Year-end promotion card for the org settings page. Preview → confirm → apply.
 * Promotion advances every student one grade on the org's curriculum ladder, so
 * it's gated behind an explicit preview + a typed academic-year + confirm, and
 * the backend apply is idempotent per academic year.
 */
import React, { useState } from "react";
import { promotePreview, promoteApply, PromotionPreview } from "../services/studentsApi";
import { EDUCATION_SYSTEMS } from "../utils/educationSystems";

const card: React.CSSProperties = { background: "#fff", border: "1px solid #e8edf3", borderRadius: 14, padding: 20, marginBottom: 16 };
const h2: React.CSSProperties = { fontSize: 16, fontWeight: 800, margin: "0 0 4px", color: "#0f172a" };
const sub: React.CSSProperties = { fontSize: 12.5, color: "#94a3b8", margin: "0 0 14px" };
const btn = (bg: string): React.CSSProperties => ({ background: bg, color: "#fff", border: "none", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" });

export default function PromotionCard({ orgCode, educationSystem }: { orgCode: string; educationSystem?: string | null }) {
  const thisYear = new Date().getFullYear();
  const [preview, setPreview] = useState<PromotionPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [academicYear, setAcademicYear] = useState(`${thisYear}-${thisYear + 1}`);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const systemLabel = EDUCATION_SYSTEMS.find((s) => s.value === (educationSystem || "").toUpperCase())?.label;

  async function runPreview() {
    setBusy(true); setError(null); setDone(null);
    try {
      setPreview(await promotePreview(orgCode));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function runApply() {
    setBusy(true); setError(null);
    try {
      const r = await promoteApply(orgCode, academicYear.trim());
      setDone(r.alreadyApplied
        ? `Already applied for ${academicYear} — nothing changed.`
        : `Promoted ${r.promoted ?? 0} student(s), ${r.graduating ?? 0} graduating.`);
      setPreview(null); setConfirming(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!educationSystem) {
    return (
      <div style={card}>
        <h2 style={h2}>Year-end promotion</h2>
        <p style={sub}>Set an education system for this school first — promotion advances students along that curriculum's grade ladder.</p>
      </div>
    );
  }

  return (
    <div style={card}>
      <h2 style={h2}>Year-end promotion</h2>
      <p style={sub}>Curriculum: <b>{systemLabel || educationSystem}</b>. Advances every student one grade; the top grade graduates (archived). Preview first — apply runs once per academic year.</p>

      {error && <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 10 }}>{error}</div>}
      {done && <div style={{ color: "#16a34a", fontSize: 13, marginBottom: 10, fontWeight: 600 }}>✓ {done}</div>}

      {!preview ? (
        <button style={btn("#3b5bdb")} disabled={busy} onClick={runPreview}>{busy ? "Loading…" : "Preview promotion"}</button>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "4px 0 12px" }}>
            <Stat k="Promoting" v={preview.totals.promoted} color="#16a34a" />
            <Stat k="Graduating" v={preview.totals.graduating} color="#7c3aed" />
            <Stat k="Skipped" v={preview.totals.skipped} color="#94a3b8" />
            <Stat k="Total" v={preview.totals.children} color="#0f172a" />
          </div>
          <div style={{ maxHeight: 180, overflow: "auto", border: "1px solid #f1f5f9", borderRadius: 8, marginBottom: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <tbody>
                {preview.rows.slice(0, 100).map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f5f7fa" }}>
                    <td style={{ padding: "7px 10px", color: "#0f172a" }}>{r.childName || r.childId}</td>
                    <td style={{ padding: "7px 10px", color: "#64748b" }}>
                      {r.skip ? <span style={{ color: "#94a3b8" }}>skipped · {r.skip}</span>
                        : r.graduated ? <span style={{ color: "#7c3aed", fontWeight: 600 }}>{r.from} → graduates</span>
                          : <span>{r.from} → <b>{r.to}</b></span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!confirming ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ fontSize: 12.5, color: "#475569" }}>Academic year</label>
              <input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: "7px 10px", fontSize: 13, width: 120 }} />
              <button style={btn("#dc2626")} disabled={busy || !academicYear.trim()} onClick={() => setConfirming(true)}>Apply promotion…</button>
              <button style={{ ...btn("#e2e8f0"), color: "#475569" }} onClick={() => setPreview(null)}>Cancel</button>
            </div>
          ) : (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 13, color: "#991b1b", marginBottom: 10 }}>
                This permanently advances <b>{preview.totals.promoted + preview.totals.graduating}</b> students for <b>{academicYear}</b>. This can't be undone in bulk. Continue?
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={btn("#dc2626")} disabled={busy} onClick={runApply}>{busy ? "Applying…" : "Yes, apply promotion"}</button>
                <button style={{ ...btn("#e2e8f0"), color: "#475569" }} disabled={busy} onClick={() => setConfirming(false)}>Go back</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ k, v, color }: { k: string; v: number; color: string }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 10, padding: "8px 14px", minWidth: 88 }}>
      <div style={{ fontSize: 20, fontWeight: 800, color }}>{v}</div>
      <div style={{ fontSize: 11, color: "#94a3b8" }}>{k}</div>
    </div>
  );
}
